/** Non-breaking space after `&` so multi-innings scores do not collapse in tight score tiles. */
export const CRICKET_COMBINED_INNINGS_SCORE_SEP = " &\u00a0";

/** Test / multi-innings display — e.g. `120 & 86/3` inside a single score box. */
export function formatCricketCombinedInningsScore(primary, secondary) {
  const p = String(primary ?? "").trim();
  const s = String(secondary ?? "").trim();
  if (!p) return "--";
  if (s && s !== "0/0" && s !== "-") {
    const head = p.split("/")[0] ?? p;
    return `${head}${CRICKET_COMBINED_INNINGS_SCORE_SEP}${s}`;
  }
  return p;
}

function findCombinedInningsSeparator(trimmed) {
  const nbspIdx = trimmed.indexOf(CRICKET_COMBINED_INNINGS_SCORE_SEP);
  if (nbspIdx >= 0) return { ampIdx: nbspIdx, sepLen: CRICKET_COMBINED_INNINGS_SCORE_SEP.length };
  const spaceIdx = trimmed.indexOf(" & ");
  if (spaceIdx >= 0) return { ampIdx: spaceIdx, sepLen: 3 };
  return { ampIdx: -1, sepLen: 0 };
}

/**
 * Parse sticky scoreboard lines like `86/3` or `120 & 86/3` (Trader / Squares / Bullion).
 * The segment after ` & ` (or the whole line) is the active innings score.
 */
export function parseScoreboardDisplayLine(line) {
  const trimmed = String(line ?? "").trim();
  if (!trimmed || trimmed === "-" || trimmed === "--") return null;

  const { ampIdx, sepLen } = findCombinedInningsSeparator(trimmed);
  const activePart = ampIdx >= 0 ? trimmed.slice(ampIdx + sepLen).trim() : trimmed;
  const slashIdx = activePart.indexOf("/");
  if (slashIdx < 0) return null;

  const runs = Number.parseInt(activePart.slice(0, slashIdx), 10);
  const wkts = Number.parseInt(activePart.slice(slashIdx + 1), 10);
  if (!Number.isFinite(runs) || !Number.isFinite(wkts)) return null;

  return {
    runs,
    wkts,
    prefix: ampIdx >= 0 ? `${trimmed.slice(0, ampIdx + 2)}${"\u00a0"}` : "",
    activePart,
  };
}

/** @param {ReturnType<typeof parseScoreboardDisplayLine>} prev */
/** @param {ReturnType<typeof parseScoreboardDisplayLine>} next */
export function deriveScoreboardOutcome(prev, next) {
  if (!prev || !next) return null;

  const runsDelta = next.runs - prev.runs;
  const wktsDelta = next.wkts - prev.wkts;
  if (runsDelta <= 0 && wktsDelta <= 0) return null;

  let bubbleLabel = null;
  if (runsDelta > 0) {
    bubbleLabel = `+${runsDelta} RUN${runsDelta === 1 ? "" : "S"}`;
  } else if (wktsDelta > 0) {
    bubbleLabel = wktsDelta === 1 ? "WICKET" : `${wktsDelta} WICKETS`;
  }

  if (!bubbleLabel) return null;

  return {
    bubbleLabel,
    runsDelta,
    wktsDelta,
    rollRuns: runsDelta > 0,
    rollWkts: wktsDelta > 0,
  };
}

/** Per-column digit pairs for slot-style roll (right-aligned). */
export function scoreboardDigitColumns(from, to) {
  const fromStr = String(from);
  const toStr = String(to);
  const len = Math.max(fromStr.length, toStr.length);
  const cols = [];

  for (let i = 0; i < len; i += 1) {
    const fromIdx = i - (len - fromStr.length);
    const toIdx = i - (len - toStr.length);
    const fromCh = fromIdx >= 0 ? fromStr[fromIdx] : null;
    const toCh = toStr[toIdx];
    cols.push({
      from: fromCh,
      to: toCh,
      animate: fromCh !== toCh,
    });
  }

  return cols;
}
